'use client';

import React, { useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, Tooltip as PieTooltip,
} from 'recharts';
import { MORTALITY_TREND_DATA, CAUSE_SUMMARY_DATA } from './mortalityData';

const CAUSE_COLORS: Record<string, string> = {
  disease: '#dc2626',
  culling: '#f59e0b',
  injury: '#f97316',
  unknown: '#9ca3af',
  predation: '#8b5cf6',
};

const AREA_KEYS = [
  { key: 'disease', label: 'Disease / Infection', color: '#dc2626' },
  { key: 'culling', label: 'Culling', color: '#f59e0b' },
  { key: 'injury', label: 'Injury / Trauma', color: '#f97316' },
  { key: 'predation', label: 'Predation', color: '#8b5cf6' },
  { key: 'unknown', label: 'Unknown', color: '#9ca3af' },
];

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-800 mb-2">{label} — Total: {total}</p>
      {payload.map(p => (
        <div key={p.name} className="flex items-center gap-2 mb-1">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
          <span className="text-gray-600">{p.name}:</span>
          <span className="font-medium text-gray-800">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function MortalityTrendsChart() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
      {/* Area Chart — 14-day trend */}
      <div className="xl:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-800">14-Day Mortality Trend by Cause</h3>
          <p className="text-xs text-gray-400 mt-0.5">Daily deaths and culling across all flocks, stacked by cause category</p>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={MORTALITY_TREND_DATA} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <defs>
              {AREA_KEYS.map(k => (
                <linearGradient key={k.key} id={`grad-${k.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={k.color} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={k.color} stopOpacity={0.03} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
            />
            {AREA_KEYS.map(k => (
              <Area
                key={k.key}
                type="monotone"
                dataKey={k.key}
                name={k.label}
                stackId="1"
                stroke={k.color}
                fill={`url(#grad-${k.key})`}
                strokeWidth={1.5}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Pie Chart — cause breakdown */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-800">Cause Breakdown</h3>
          <p className="text-xs text-gray-400 mt-0.5">Last 30 days — all flocks</p>
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <PieChart>
            <Pie
              data={CAUSE_SUMMARY_DATA}
              cx="50%"
              cy="50%"
              innerRadius={45}
              outerRadius={70}
              paddingAngle={2}
              dataKey="count"
              onMouseEnter={(_, index) => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              {CAUSE_SUMMARY_DATA.map((entry, index) => (
                <Cell
                  key={entry.cause}
                  fill={entry.color}
                  opacity={activeIndex === null || activeIndex === index ? 1 : 0.5}
                  stroke="none"
                />
              ))}
            </Pie>
            <PieTooltip
              formatter={(value: number, name: string) => [`${value} birds`, name]}
              contentStyle={{ fontSize: '11px', borderRadius: '8px', border: '1px solid #e5e7eb' }}
            />
          </PieChart>
        </ResponsiveContainer>
        {/* Legend */}
        <div className="space-y-1.5 mt-2">
          {CAUSE_SUMMARY_DATA.map(c => (
            <div key={c.cause} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
                <span className="text-gray-600 truncate max-w-[130px]">{c.cause}</span>
              </div>
              <span className="font-semibold text-gray-800 ml-2">{c.percentage}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
